import { useEffect, useState } from 'react';

// Detects whether the BrainSync TWA is installed on the user's device while
// they're browsing the web (mobile Chrome / non-standalone).
//
// Why: when the TWA is installed, Android's App Links intercept Google OAuth's
// final redirect to `brainsync.pro/auth/google` and deep-link it into the app.
// The login succeeds inside the TWA, but the originating Chrome tab is left
// stuck on Google's "Choose an account" loader. By detecting this state up
// front we can swap the "Sign in with Google" button for a direct
// "Open in BrainSync App" deep-link, which hands the user cleanly off to
// the app without ever starting an OAuth flow that's destined to be hijacked.
//
// Detection uses `navigator.getInstalledRelatedApps()` (Chrome on Android only).
// Requires `related_applications` in the web manifest to point to the TWA's
// Play Store id - both legacy (`com.brainsync.app`, sideloaded APKs v1.3.x)
// and the live Play Store package (`com.brainsync.myapp`) are listed.
const TWA_PACKAGE_IDS = ['com.brainsync.myapp', 'com.brainsync.app'];

export const useInstalledTWA = () => {
    // null = unknown (still checking / not supported), otherwise the matched
    // package id string (truthy) or `false` when nothing's installed.
    const [installedPackage, setInstalledPackage] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                // If we're already running inside the TWA / a standalone PWA,
                // there's nothing to "open in app" - the user is already there.
                const isStandalone =
                    window.matchMedia('(display-mode: standalone)').matches
                    || window.navigator.standalone === true
                    || document.referrer.includes('android-app://');
                if (isStandalone) {
                    if (!cancelled) setInstalledPackage(false);
                    return;
                }

                if (typeof navigator.getInstalledRelatedApps !== 'function') {
                    if (!cancelled) setInstalledPackage(false);
                    return;
                }
                const apps = await navigator.getInstalledRelatedApps();
                const found = Array.isArray(apps)
                    ? apps.find(
                        (a) => a && a.platform === 'play' && TWA_PACKAGE_IDS.includes(a.id),
                    )
                    : null;
                if (!cancelled) setInstalledPackage(found ? found.id : false);
            } catch (_) {
                if (!cancelled) setInstalledPackage(false);
            }
        };
        check();
        return () => { cancelled = true; };
    }, []);

    return installedPackage;
};

// Builds the Android intent URL that opens the BrainSync TWA at the given
// in-app path. Pass the package id returned by `useInstalledTWA` so we
// deep-link into the same app the user actually has installed (Play Store
// `com.brainsync.myapp` or legacy sideloaded `com.brainsync.app`). On
// non-Chrome browsers the intent fragment is ignored and the URL just
// navigates normally - harmless fallback.
//
// `S.browser_fallback_url` keeps users without the TWA from hitting a
// "Sorry, no app installed" Chrome error - they're sent to the Play Store
// listing instead so they can install the app.
export const buildTWAIntentUrl = (path = '/dashboard', packageId = 'com.brainsync.myapp') => {
    const safePath = path.startsWith('/') ? path : `/${path}`;
    const fallback = encodeURIComponent(
        `https://play.google.com/store/apps/details?id=${packageId === 'com.brainsync.app' ? 'com.brainsync.myapp' : packageId}`,
    );
    return `intent://brainsync.pro${safePath}#Intent;scheme=https;package=${packageId};S.browser_fallback_url=${fallback};end`;
};
